import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import { CustomerUploadResponse, ScrewPrediction, PropertyValue } from "../../types/CustomerUpload";
import Modal from "../../components/Modal";
import SelectDropDown from "../../components/SelectDropdown";
import { formatDate } from "../../utils/formatDate";
import { api } from "../../services/Api";
import { getScrewsPropertiesAction } from "../../store/actions/ScrewsActions";
import { getScrewProperties } from "../../store/selectors/ScrewSelectors";
import type { CategoricalValueOption, ScrewProperty } from "../../types/ScrewTypes";
import errorOptions from "./ErrorOptions";
import "./Evaluate.scss";
import "../../components/Modal/Modal.scss";

interface UserInputEntry {
  propertyid: string;
  propertyname: string;
  categoricalvalueid: string;
  categoricalvalue: string;
}

interface UserInputPayload {
  properties: UserInputEntry[];
  notes: string;
}

const parseUserInput = (raw: string | null | undefined): UserInputPayload | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInputPayload;
  } catch {
    return null;
  }
};

const UserInputSection: React.FC<{ userinput: string | null | undefined }> = ({ userinput }) => {
  const parsed = parseUserInput(userinput);
  if (!parsed) return null;

  const hasProperties = parsed.properties && parsed.properties.length > 0;
  const hasNotes = parsed.notes && parsed.notes.trim().length > 0;

  if (!hasProperties && !hasNotes) return null;

  return (
    <div className="user-input-section">
      <h4 className="user-input-title">Customer input</h4>
      {hasProperties && (
        <div className="user-input-properties">
          {parsed.properties.map((entry) => (
            <div key={entry.propertyid} className="user-input-item">
              <span className="user-input-prop-name">{entry.propertyname}</span>
              <span className="user-input-prop-value">{entry.categoricalvalue}</span>
            </div>
          ))}
        </div>
      )}
      {hasNotes && (
        <div className="user-input-notes">
          <span className="user-input-prop-name">Remarks</span>
          <span className="user-input-prop-value">{parsed.notes}</span>
        </div>
      )}
    </div>
  );
};

const ScrewPredictionDetailsCard: React.FC<{
  prediction: ScrewPrediction;
  index: number;
  isFromIndex: boolean;
  isToIndex: boolean;
  onSelect: () => void;
}> = ({ prediction, index, isFromIndex, isToIndex, onSelect }) => {
  const getSelectionClass = () => {
    if (isFromIndex && isToIndex) return "selected-to";
    if (isFromIndex) return "selected-from";
    if (isToIndex) return "selected-to";
    return "";
  };

  return (
    <div
      className={`prediction-details ${getSelectionClass()}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
    >
      <div className="prediction-header">
        <span className="prediction-number">Prediction {index + 1}</span>
        {(isFromIndex || isToIndex) && (
          <span className={`selection-type ${isFromIndex && isToIndex ? "hidden" : ""}`}>
            {isFromIndex && isToIndex ? "" : isFromIndex ? "Current" : "New"}
          </span>
        )}
      </div>
      <div className="prediction-content">
        <div className="prediction-item">
          <div className="label">Confidence</div>
          <div className="value">{prediction.confidence.toFixed(1)}%</div>
        </div>
        {prediction.categoricalvalue && (
          <div className="prediction-item">
            <div className="label">Ref</div>
            <div className="value">{prediction.categoricalvalue}</div>
          </div>
        )}
        {prediction.propertyvalues?.map((pv, i) => (
          <div key={i} className="prediction-item">
            <div className="label">{pv.propertyname}</div>
            <div className="value">
              {pv.categoricalvalue ?? (pv.numericalvalue != null ? String(pv.numericalvalue) : "-")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ScrewPredictionCard: React.FC<{
  prediction: CustomerUploadResponse;
  onAccept: (prediction: CustomerUploadResponse, selectedIndex: number) => void;
  onChange: (prediction: CustomerUploadResponse, fromIndex: number, toIndex: number) => void;
  onError: (prediction: CustomerUploadResponse) => void;
  onCustomChange: (prediction: CustomerUploadResponse, toIndex: number) => void;
}> = ({ prediction, onAccept, onChange, onError, onCustomChange }) => {
  const preds = prediction.screwpredictions ?? [];
  const initialIndex = 0;
  const [fromIndex] = useState<number>(initialIndex);
  const [toIndex, setToIndex] = useState<number>(initialIndex);

  const handlePredictionSelect = (index: number) => {
    setToIndex(index);
  };

  const isAcceptDisabled = fromIndex !== toIndex || preds.length === 0;
  const hasChangedVerifyMessage =
    prediction.verifymessage?.toLowerCase().includes("to custom");

  return (
    <div className="prediction-card">
      <div className="card-header">
        <div className="customer-info">
          <div>
            <span className="label">Customer:</span>
            <span className="value">{prediction.clientname}</span>
          </div>
          <div>
            <span className="label">Uploaded:</span>
            <span className="value">{formatDate(prediction.dateuploaded)}</span>
          </div>
          {prediction.dateverified && (
            <div>
              <span className="label">Verified:</span>
              <span className="value">
                {formatDate(prediction.dateverified)} by {prediction.verifieremail}
              </span>
            </div>
          )}
        </div>
        <div className="action-buttons">
          {preds.length > 0 && (
            <button
              className={`accept ${isAcceptDisabled ? "disabled" : ""}`}
              onClick={() => onAccept(prediction, toIndex)}
              disabled={isAcceptDisabled}
            >
              Accept
            </button>
          )}
          <button className="custom-change" onClick={() => onCustomChange(prediction, toIndex)}>
            {preds.length === 0 ? "Custom" : "Change"}
          </button>
          <button className="error" onClick={() => onError(prediction)}>
            Error
          </button>
        </div>
      </div>
      <div className="card-content">
        <div className="image-container">
          <img src={prediction.path} alt="Screw" />
        </div>
        <UserInputSection userinput={prediction.userinput} />
        {prediction.status === "PREDICTION_FAILED" ? (
          <div className="error-message">
            <p>Something went wrong with this prediction. The model could not return any predictions.</p>
          </div>
        ) : prediction.status === "UPLOADED" ? (
          <div className="error-message">
            <p>Please wait until the model has made a prediction.</p>
          </div>
        ) : hasChangedVerifyMessage ? (
          <div className="verify-message">
            <p>{prediction.verifymessage}</p>
          </div>
        ) : prediction.status === "FAILED" ? (
          <div className="verify-message">
            <p>{prediction.verifymessage}</p>
          </div>
        ) : (
          <div className="predictions-container">
            {preds.map((pred, idx) => (
              <ScrewPredictionDetailsCard
                key={idx}
                prediction={pred}
                index={idx}
                isFromIndex={idx === fromIndex}
                isToIndex={idx === toIndex}
                onSelect={() => handlePredictionSelect(idx)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EvaluateScrews: React.FC = () => {
  const dispatch = useAppDispatch();
  const screwProperties = useSelector(getScrewProperties);
  const [predictions, setPredictions] = useState<CustomerUploadResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(true);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: "accept" | "change" | "error" | "custom-change" | null;
    prediction: CustomerUploadResponse | null;
    fromIndex: number;
    toIndex: number;
  }>({ isOpen: false, type: null, prediction: null, fromIndex: 0, toIndex: 0 });
  const [customScrewValues, setCustomScrewValues] = useState<{
    categoricalvalueid: string;
    categoricalvalue: string;
  }>({ categoricalvalueid: "", categoricalvalue: "" });
  const [refMetadata, setRefMetadata] = useState<PropertyValue[] | null>(null);
  const [refMetadataLoading, setRefMetadataLoading] = useState(false);
  const [selectedError, setSelectedError] = useState<string>("");
  const [customError, setCustomError] = useState<string>("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    dispatch(getScrewsPropertiesAction());
  }, [dispatch]);

  useEffect(() => {
    fetchPredictions();
  }, []);

  const sortOpts = (opts: CategoricalValueOption[]) =>
    opts
      .slice()
      .sort((a: CategoricalValueOption, b: CategoricalValueOption) =>
        (a.categoricalvalue ?? "").localeCompare(b.categoricalvalue ?? "", undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
      .map((o: CategoricalValueOption) => ({
        value: o.categoricalvalueid,
        label: o.categoricalvalue ?? o.categoricalvalueid,
      }));

  const refOptions = useMemo(() => {
    if (!screwProperties?.items) return [];
    const refProperty = screwProperties.items.find(
      (p: ScrewProperty) => p.islabel || p.propertyname === "ref"
    );
    const opts: CategoricalValueOption[] = refProperty?.categoricalvalueoptions ?? [];
    return [{ value: "", label: "None" }, ...sortOpts(opts)];
  }, [screwProperties]);


  const fetchPredictions = async () => {
    try {
      setLoading(true);
      const response = await api.get("/client-uploads?model_type=screw");
      setPredictions(response);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch predictions");
    } finally {
      setLoading(false);
    }
  };

  const filteredPredictions = predictions.filter(
    (pred) => !showUnverifiedOnly || !pred.dateverified
  );

  const handleUpdatePrediction = async (
    clientuploadid: string,
    status: string,
    verifiedcategoricalvalueid?: string,
    verifymessage?: string
  ) => {
    try {
      await api.put(`/client-uploads/${clientuploadid}`, {
        status,
        verifiedcategoricalvalueid,
        verifymessage,
      });
      await fetchPredictions();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update prediction");
    }
  };

  const handleAcceptClick = (prediction: CustomerUploadResponse, selectedIndex: number) => {
    setModalState({
      isOpen: true,
      type: "accept",
      prediction,
      fromIndex: selectedIndex,
      toIndex: selectedIndex,
    });
  };

  const fetchRefMetadata = async (categoricalvalueid: string) => {
    if (!categoricalvalueid) {
      setRefMetadata(null);
      return;
    }
    setRefMetadataLoading(true);
    try {
      const data = await api.get(`/datasets/screws/categoricalvalues/${categoricalvalueid}/propertyvalues`);
      setRefMetadata((data?.items as PropertyValue[]) ?? null);
    } catch {
      setRefMetadata(null);
    } finally {
      setRefMetadataLoading(false);
    }
  };

  const handleCustomChangeClick = (prediction: CustomerUploadResponse, toIndex: number) => {
    const preds = prediction.screwpredictions ?? [];
    const initial = preds[toIndex];
    const refId = initial?.categoricalvalueid ?? "";
    const refLabel = initial?.categoricalvalue ?? initial?.categoricalvalueid ?? "";
    setCustomScrewValues({ categoricalvalueid: refId, categoricalvalue: refLabel });
    setRefMetadata(null);
    if (refId) fetchRefMetadata(refId);
    setModalState({
      isOpen: true,
      type: "custom-change",
      prediction,
      fromIndex: toIndex,
      toIndex,
    });
  };

  const handleChangeClick = (
    prediction: CustomerUploadResponse,
    fromIndex: number,
    toIndex: number
  ) => {
    setModalState({ isOpen: true, type: "change", prediction, fromIndex, toIndex });
  };

  const handleErrorClick = (prediction: CustomerUploadResponse) => {
    setModalState({
      isOpen: true,
      type: "error",
      prediction,
      fromIndex: 0,
      toIndex: 0,
    });
    setSelectedError("");
    setCustomError("");
  };

  const handleConfirm = async () => {
    if (!modalState.prediction || confirming) return;
    setConfirming(true);
    const preds = modalState.prediction.screwpredictions ?? [];

    switch (modalState.type) {
      case "accept": {
        const selected = preds[modalState.fromIndex];
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          "VERIFIED",
          selected.categoricalvalueid,
          "Prediction accepted."
        );
        break;
      }
      case "custom-change": {
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          "VERIFIED",
          customScrewValues.categoricalvalueid,
          `Prediction changed to custom ref: ${customScrewValues.categoricalvalue}`
        );
        break;
      }
      case "change": {
        const selected = preds[modalState.toIndex];
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          "VERIFIED",
          selected.categoricalvalueid,
          `Changed to ${selected.categoricalvalue ?? selected.categoricalvalueid}`
        );
        break;
      }
      case "error": {
        const errorMsg =
          selectedError === "other"
            ? customError
            : [
                errorOptions.find((o) => o.id === selectedError)?.mail_message_nl,
                errorOptions.find((o) => o.id === selectedError)?.mail_message_fr,
                errorOptions.find((o) => o.id === selectedError)?.mail_message_en,
                errorOptions.find((o) => o.id === selectedError)?.mail_message_de,
              ].join(" ||| ");
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          "FAILED",
          undefined,
          errorMsg ?? ""
        );
        break;
      }
    }
    setConfirming(false);
    handleClose();
  };

  const handleClose = () => {
    setModalState({ isOpen: false, type: null, prediction: null, fromIndex: 0, toIndex: 0 });
    setCustomScrewValues({ categoricalvalueid: "", categoricalvalue: "" });
    setRefMetadata(null);
    setSelectedError("");
    setCustomError("");
    setConfirming(false);
  };

  return (
    <div>
      <div className="dashboard-header">
        <h1>Evaluate Screws</h1>
        <div className="dashboard-controls">
          <label className="filter-control">
            <input
              type="checkbox"
              checked={showUnverifiedOnly}
              onChange={(e) => setShowUnverifiedOnly(e.target.checked)}
            />
            Show unverified only
          </label>
          <button className="btn refresh-button" onClick={fetchPredictions}>
            Refresh
          </button>
        </div>
      </div>
      {error && (
        <div className="alert-message">
          <p>{error}</p>
        </div>
      )}
      {loading ? (
        <div className="loading-message">Loading predictions...</div>
      ) : (
        <div className="predictions-list">
          {filteredPredictions.map((prediction) => (
            <ScrewPredictionCard
              key={prediction.clientuploadid}
              prediction={prediction}
              onAccept={handleAcceptClick}
              onCustomChange={handleCustomChangeClick}
              onChange={handleChangeClick}
              onError={handleErrorClick}
            />
          ))}
        </div>
      )}
      <Modal isOpen={modalState.isOpen} onClose={handleClose}>
        {modalState.type === "accept" && modalState.prediction && (
          <>
            <div className="modal-header">
              <h2>Confirm Accept</h2>
              <p>Are you sure you want to accept this prediction?</p>
            </div>
            <div className="modal-body">
              <div className="image-container">
                <img src={modalState.prediction.path} alt="Screw" />
              </div>
              {(() => {
                const sel = modalState.prediction.screwpredictions?.[modalState.fromIndex];
                return (
                  <div className="ref-metadata">
                    <p className="ref-metadata-title">Prediction details</p>
                    <div className="ref-metadata-list">
                      <div className="ref-metadata-item">
                        <span className="ref-metadata-label">Ref:</span>
                        <span className="ref-metadata-value">{sel?.categoricalvalue ?? sel?.categoricalvalueid}</span>
                      </div>
                      <div className="ref-metadata-item">
                        <span className="ref-metadata-label">Confidence:</span>
                        <span className="ref-metadata-value">{sel != null ? `${sel.confidence.toFixed(1)}%` : ""}</span>
                      </div>
                      {sel?.propertyvalues?.map((pv) => (
                        <div key={pv.propertyid} className="ref-metadata-item">
                          <span className="ref-metadata-label">{pv.propertyname}:</span>
                          <span className="ref-metadata-value">
                            {pv.categoricalvalue ?? (pv.numericalvalue != null ? String(pv.numericalvalue) : "—")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="cancel" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button className="accept" onClick={handleConfirm} disabled={confirming}>
                {confirming ? "Accepting..." : "Yes, Accept"}
              </button>
            </div>
          </>
        )}
        {modalState.type === "custom-change" && modalState.prediction && (
          <>
            <div className="modal-header">
              <h2>Choose ref</h2>
              <p>Select the correct ref for this screw:</p>
            </div>
            <div className="modal-body">
              <div className="image-container">
                <img src={modalState.prediction.path} alt="Screw" />
              </div>
              <div className="options">
                <div className="row bottom-10">
                  <SelectDropDown
                    hasCreateNewFilter={false}
                    onChange={(evt: React.ChangeEvent<HTMLSelectElement>) => {
                      const id = evt.target?.value ?? "";
                      const option = refOptions.find((o) => o.value === id);
                      setCustomScrewValues({
                        categoricalvalueid: id,
                        categoricalvalue: option?.label ?? id,
                      });
                      fetchRefMetadata(id);
                    }}
                    options={refOptions}
                    label="Ref"
                    selectedOption={customScrewValues.categoricalvalueid}
                  />
                </div>
                {customScrewValues.categoricalvalueid && (
                  <div className="ref-metadata">
                    <p className="ref-metadata-title">Properties for this ref:</p>
                    {refMetadataLoading ? (
                      <p className="ref-metadata-loading">Loading...</p>
                    ) : refMetadata && refMetadata.length > 0 ? (
                      <div className="ref-metadata-list">
                        {refMetadata
                          .filter((pv) => !pv.islabel)
                          .map((pv) => (
                            <div key={pv.propertyid} className="ref-metadata-item">
                              <span className="ref-metadata-label">{pv.propertyname}:</span>
                              <span className="ref-metadata-value">
                                {pv.categoricalvalue ?? (pv.numericalvalue != null ? String(pv.numericalvalue) : "—")}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="ref-metadata-empty">No metadata available for this ref.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button
                className="custom-change"
                onClick={handleConfirm}
                disabled={!customScrewValues.categoricalvalueid || confirming}
              >
                {confirming ? "Saving..." : "Confirm Change"}
              </button>
            </div>
          </>
        )}
        {modalState.type === "change" && modalState.prediction && (
          <>
            <div className="modal-header">
              <h2>Choose prediction</h2>
              <p>Select the screw prediction to use:</p>
            </div>
            <div className="modal-body">
              <div className="image-container">
                <img src={modalState.prediction.path} alt="Screw" />
              </div>
              <div className="predictions-container">
                {modalState.prediction.screwpredictions?.map((sp, idx) => (
                  <ScrewPredictionDetailsCard
                    key={idx}
                    prediction={sp}
                    index={idx}
                    isFromIndex={idx === modalState.fromIndex}
                    isToIndex={idx === modalState.toIndex}
                    onSelect={() =>
                      setModalState((s) => ({ ...s, toIndex: idx }))
                    }
                  />
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button className="custom-change" onClick={handleConfirm} disabled={confirming}>
                {confirming ? "Saving..." : "Confirm Change"}
              </button>
            </div>
          </>
        )}
        {modalState.type === "error" && (
          <>
            <div className="modal-header">
              <h2>Report Error</h2>
              <p>What went wrong?</p>
            </div>
            <div className="modal-body">
              <div className="image-container">
                <img src={modalState.prediction?.path} alt="Screw" />
              </div>
              <div className="error-options">
                <div className="radio-group">
                  {errorOptions.map((option) => (
                    <div key={option.id} className="radio-option">
                      <input
                        type="radio"
                        id={`screw-${option.id}`}
                        checked={selectedError === option.id}
                        onChange={() => setSelectedError(option.id)}
                      />
                      <label htmlFor={`screw-${option.id}`}>{option.label}</label>
                    </div>
                  ))}
                </div>
                {selectedError === "other" && (
                  <input
                    type="text"
                    placeholder="Please specify the error"
                    value={customError}
                    className="custom-error-input"
                    onChange={(e) => setCustomError(e.target.value)}
                  />
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button
                className="error"
                onClick={handleConfirm}
                disabled={!selectedError || (selectedError === "other" && !customError) || confirming}
              >
                {confirming ? "Submitting..." : "Submit"}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default EvaluateScrews;
